import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GrantRole, NewAoTeam, Scenario, SharingState, ViewerId } from './types';

const BASE = '/api/network-sharing';
const KEY = ['network-sharing'];

async function send(path: string, method: string, viewer: ViewerId | null, body?: unknown): Promise<SharingState> {
    const res = await fetch(`${BASE}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', ...(viewer ? { 'X-Viewer': viewer } : {}) },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail ?? 'Erreur lors de l’enregistrement');
    return data as SharingState;
}

export function useSharingState() {
    return useQuery({
        queryKey: KEY,
        queryFn: async () => {
            const res = await fetch(`${BASE}/state`);
            if (!res.ok) throw new Error('Impossible de charger les accès du réseau');
            return (await res.json()) as SharingState;
        },
    });
}

export function useSharingActions(viewer: ViewerId) {
    const qc = useQueryClient();
    const onSuccess = (data: SharingState) => qc.setQueryData(KEY, data);
    const share = useMutation({ mutationFn: (v: { principalIds: string[]; role: GrantRole }) => send('/grants', 'POST', viewer, v), onSuccess });
    const changeRole = useMutation({ mutationFn: (v: { principalId: string; role: GrantRole }) => send(`/grants/${v.principalId}`, 'PATCH', viewer, { role: v.role }), onSuccess });
    const revoke = useMutation({ mutationFn: (principalId: string) => send(`/grants/${principalId}`, 'DELETE', viewer), onSuccess });
    const createTeam = useMutation({ mutationFn: (team: NewAoTeam) => send('/ao-teams', 'POST', viewer, team), onSuccess });
    const transfer = useMutation({ mutationFn: (userId: string) => send('/owner', 'POST', viewer, { userId }), onSuccess });
    const requestAccess = useMutation({ mutationFn: (page?: string) => send('/requests', 'POST', viewer, { page }), onSuccess });
    const acceptRequest = useMutation({ mutationFn: (v: { userId: string; role: GrantRole }) => send(`/requests/${v.userId}/accept`, 'POST', viewer, { role: v.role }), onSuccess });
    const declineRequest = useMutation({ mutationFn: (userId: string) => send(`/requests/${userId}`, 'DELETE', viewer), onSuccess });
    const reset = useMutation({ mutationFn: (scenario: Scenario) => send('/reset', 'POST', null, { scenario }), onSuccess });
    return { share, changeRole, revoke, createTeam, transfer, requestAccess, acceptRequest, declineRequest, reset };
}

export type SharingActions = ReturnType<typeof useSharingActions>;
